import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons/faTrashCan';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { useOutreachLists } from '../hooks/useOutreachLists';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import type { ContactFilters } from '../hooks/useContacts';
import { contactsMessages as messages } from './contactsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Outreach area; each part uses its own slice of it */
import styles from './Contacts.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ContactsSidebarProps {
  accountId: number;
  filters: ContactFilters;
  onChange: (patch: Partial<ContactFilters>) => void;
}

/**
 * The lists of Contacts (design outreach q1 left column; a chip row on a phone, q6). The status
 * filters are the chips of `ContactsFilterBar` (design outreach-v2 k1). No list: nothing shows.
 */
const ContactsSidebarComponent: React.FC<ContactsSidebarProps> = ({ accountId, filters, onChange }) => {
  const intl = useIntl();
  const { isDemo } = useOutreachPlan();
  const { lists, deleteList } = useOutreachLists(accountId);

  const listItems = useMemo(
    () =>
      lists.map((list) => (
        <div key={list.listUuid} className="d-flex align-items-center">
          <button
            type="button"
            className={clsx(styles.sideItem, filters.listUuid === list.listUuid && styles.sideOn)}
            onClick={() => onChange({ listUuid: list.listUuid, status: null, source: null })}
            data-testid="contacts-list-item"
          >
            {list.name}
            <span className={styles.sideCount}>{intl.formatNumber(list.count)}</span>
          </button>
          {!isDemo && (
            <button
              type="button"
              className="btn btn-link btn-sm text-body-secondary"
              aria-label={intl.formatMessage(messages.deleteList, { name: list.name })}
              onClick={() => {
                // eslint-disable-next-line no-alert -- a short confirm before a delete
                if (window.confirm(intl.formatMessage(messages.deleteListConfirm, { name: list.name }))) {
                  deleteList(list.listUuid).catch(console.error);
                  if (filters.listUuid === list.listUuid) {
                    onChange({ listUuid: null });
                  }
                }
              }}
            >
              <FontAwesomeIcon icon={faTrashCan} />
            </button>
          )}
        </div>
      )),
    [deleteList, filters.listUuid, intl, isDemo, lists, onChange]
  );

  if (lists.length === 0) {
    return null;
  }
  return (
    <nav className={styles.side} aria-label={intl.formatMessage(messages.listsTitle)}>
      <div className={styles.sideHead}>{intl.formatMessage(messages.listsTitle)}</div>
      {listItems}
    </nav>
  );
};

export const ContactsSidebar = TypedMemo(ContactsSidebarComponent);
